import React, { useState } from "react";
import "../../css/DocumentUpload.css";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "../../utils/toastUtils";
import {
  addFiles,
  addUrls,
  addVideos,
  setStatus,
} from "../../redux/uploadQueueSlice";
import mammoth from "mammoth";
import api from "../../api";
import uploadIcon from "../../assets/UserIcons/UploadFileIcon.svg";
import crossIcon from "../../assets/UserIcons/Cancel.svg";
import linkIcon from "../../assets/UserIcons/Website.svg";
import videoIcon from "../../assets/UserIcons/VideoIcon.svg";
import fileIcon from "../../assets/UserIcons/FileIcon.svg";
import { validateFileName } from "../../utils/validateFileName";
import UploadFolderModal from "./UploadFolderModal";

const DocumentUpload = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [uploadMode, setUploadMode] = useState("Global");
  const { selectedModel } = useSelector((state) => state.model);
  const isFullPrivacy = false;
  const [showFirstModal, setShowFirstModal] = useState(false);

  const [showFolderModal, setShowFolderModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [folderItems, setFolderItems] = useState([]);

  const [selectedFolderId, setSelectedFolderId] = useState({
    id: null,
    name: "Root",
  });

  const [isFetchingFolders, setIsFetchingFolders] = useState(false);

  const [createFolderName, setCreateFolderName] = useState("");

  const [showCreateFolderInput, setShowCreateFolderInput] = useState(false);

  const [isCreatingFolder, setIsCreatingFolder] = useState(false);

  const [urlLinks, setUrlLinks] = useState([""]);
  const [videoLinks, setVideoLinks] = useState([""]);
  const [activeUpload, setActiveUpload] = useState("files"); // "url" | "video"
  const [uploadStatus, setUploadStatus] = useState("uploading");
  const [uploadedInfo, setUploadedInfo] = useState({ type: "", count: 0 });
  const dispatch = useDispatch();
  const [totalPages, setTotalPages] = useState(0);
  const [Description, setdescription] = useState("");

  // 🌐 Define endpoints for both modes
  const API_BASE = process.env.REACT_APP_API_URL;

  const getDocxPages = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    const text = result.value || "";
    const words = text.trim().split(/\s+/).length;

    return Math.ceil(words / 300);
  };

  const getTxtPages = async (file) => {
    const text = await file.text();
    const words = text.trim().split(/\s+/).length;

    return Math.ceil(words / 300);
  };

  const getImagePages = () => 1;

  const getFilePages = async (file) => {
    const fileType = file.type;

    // 1. PDF → exclude
    if (fileType === "application/pdf") {
      return 0;
    }

    // 2. DOCX
    if (
      fileType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return await getDocxPages(file);
    }

    // 3. TXT
    if (fileType === "text/plain") {
      return await getTxtPages(file);
    }

    // 4. Images
    if (fileType.startsWith("image/")) {
      return getImagePages();
    }

    // Default → count as 1
    return 1;
  };

  // Disappear when the input is focused
  const [focusedIndex, setFocusedIndex] = useState(null);

  // ✅ Remove single file
  const handleRemove = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Allowed extensions
  const allowedExtensions = [
    "pdf",
    "docx",
    "txt",
    "doc",
    "pptx",
    "xlsx",
    "mp3",
    "wav",
    "aac",
    "flac",
    "mp4",
    "mov",
    "avi",
    "mkv",
    "flv",
    "png",
    "jpg",
    "jpeg",
    "gif",
    "bmp",
    "tiff",
  ];

  /**
   * Get per-file-type size limit in bytes.
   */
  const getSizeLimit = (filename) => {
    const ext = filename.split(".").pop().toLowerCase();

    // ZIP/RAR archives: 400 MB
    if (ext === "zip" || ext === "rar") {
      return 400 * 1024 * 1024;
    }

    // PPT/PPTX and DOC/DOCX: 100 MB
    if (
      ext === "ppt" ||
      ext === "pptx" ||
      ext === "doc" ||
      ext === "docx"
    ) {
      return 100 * 1024 * 1024;
    }

    // All other files: 50 MB
    return 50 * 1024 * 1024;
  };

  /**
   * Get human-readable size limit string for error messages.
   */
  const getSizeLimitLabel = (filename) => {
    const ext = filename.split(".").pop().toLowerCase();

    if (ext === "zip" || ext === "rar") return "400 MB";
    if (
      ext === "ppt" ||
      ext === "pptx" ||
      ext === "doc" ||
      ext === "docx"
    )
      return "100 MB";

    return "50 MB";
  };

  const filterSupportedFiles = (files) => {
    const supported = [];
    const unsupported = [];
    const oversized = [];

    files.forEach((file) => {
      const ext = file.name.split(".").pop().toLowerCase();

      // Check extension support
      if (!allowedExtensions.includes(ext)) {
        unsupported.push(file);
        return;
      }

      // Check size limit per file type
      const sizeLimit = getSizeLimit(file.name);
      if (file.size > sizeLimit) {
        oversized.push({
          name: file.name,
          size: file.size,
          limitLabel: getSizeLimitLabel(file.name),
        });
        return;
      }

      supported.push(file);
    });

    return { supported, unsupported, oversized };
  };

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);

    const { supported, unsupported, oversized } =
      filterSupportedFiles(selectedFiles);

    if (unsupported.length > 0) {
      const names = unsupported.map((f) => f.name).join(", ");
      showErrorToast(`Unsupported file(s): ${names}`);
    }

    if (oversized.length > 0) {
      oversized.forEach((f) => {
        const sizeMB = (f.size / (1024 * 1024)).toFixed(1);
        showErrorToast(
          `"${f.name}" (${sizeMB} MB) exceeds the ${f.limitLabel} size limit.`,
        );
      });
    }

    if (supported.length === 0) return;

    // Enforce maximum combined items (files + urls + videos)
    const existingUrlsCount = urlLinks.filter((u) => u.trim() !== "").length;
    const existingVideosCount = videoLinks.filter(
      (v) => v.trim() !== "",
    ).length;
    const currentCount = files.length + existingUrlsCount + existingVideosCount;
    const MAX_ITEMS = 20;

    if (currentCount >= MAX_ITEMS) {
      showErrorToast(
        `You can upload a maximum of ${MAX_ITEMS} items at a time.`,
      );
      return;
    }

    let allowed = supported;
    if (currentCount + supported.length > MAX_ITEMS) {
      const remaining = MAX_ITEMS - currentCount;
      allowed = supported.slice(0, remaining);
      showInfoToast(`Added ${remaining} files (limit ${MAX_ITEMS} items).`);
    }

    let newTotal = totalPages;

    for (const file of allowed) {
      const pages = await getFilePages(file);
      newTotal += pages;
    }

    setTotalPages(newTotal);
    setFiles((prev) => [...prev, ...allowed]);

    // 🔥 THIS FIXES THE DOUBLE-TRIGGER PROBLEM
    setTimeout(() => {
      e.target.value = null;
    }, 0);
  };

  const handleDrop = async (e) => {
    e.preventDefault();

    const droppedFiles = Array.from(e.dataTransfer.files);

    const { supported, unsupported, oversized } =
      filterSupportedFiles(droppedFiles);

    if (unsupported.length > 0) {
      const names = unsupported.map((f) => f.name).join(", ");
      showErrorToast(`Unsupported file(s): ${names}`);
    }

    if (oversized.length > 0) {
      oversized.forEach((f) => {
        const sizeMB = (f.size / (1024 * 1024)).toFixed(1);
        showErrorToast(
          `"${f.name}" (${sizeMB} MB) exceeds the ${f.limitLabel} size limit.`,
        );
      });
    }

    if (supported.length === 0) return;

    // Enforce maximum combined items (files + urls + videos)
    const existingUrlsCount = urlLinks.filter((u) => u.trim() !== "").length;
    const existingVideosCount = videoLinks.filter(
      (v) => v.trim() !== "",
    ).length;
    const currentCount = files.length + existingUrlsCount + existingVideosCount;
    const MAX_ITEMS = 20;

    if (currentCount >= MAX_ITEMS) {
      showErrorToast(
        `You can upload a maximum of ${MAX_ITEMS} items at a time.`,
      );
      return;
    }

    let allowed = supported;
    if (currentCount + supported.length > MAX_ITEMS) {
      const remaining = MAX_ITEMS - currentCount;
      allowed = supported.slice(0, remaining);
      showInfoToast(`Added ${remaining} files (limit ${MAX_ITEMS} items).`);
    }

    let newTotal = totalPages;

    for (const file of allowed) {
      const pages = await getFilePages(file);
      newTotal += pages;
    }

    setTotalPages(newTotal);
    setFiles((prev) => [...prev, ...allowed]);
  };

  const handleDragOver = (e) => e.preventDefault();

  // this will run onclick upload button
  const handleSubmit = () => {
    const isYouTube = (link) =>
      link.includes("youtube.com") || link.includes("youtu.be");

    const isValidHttpUrl = (url) => {
      try {
        const u = new URL(url);
        return ["http:", "https:"].includes(u.protocol);
      } catch {
        return false;
      }
    };

    const trimmedUrls = urlLinks.map((l) => l.trim());
    const trimmedVideos = videoLinks.map((l) => l.trim());

    // Enforce maximum combined items (files + urls + videos)
    const nonEmptyUrls = trimmedUrls.filter((u) => u !== "");
    const nonEmptyVideos = trimmedVideos.filter((v) => v !== "");
    const MAX_ITEMS = 20;
    const totalSelected =
      files.length + nonEmptyUrls.length + nonEmptyVideos.length;
    if (totalSelected > MAX_ITEMS) {
      showErrorToast(
        `You can upload a maximum of ${MAX_ITEMS} items at a time. Please reduce your selection.`,
      );
      setUploadStatus("error");
      return;
    }

    // --------------------------------------------------
    // ✅ WEB TAB VALIDATION
    // --------------------------------------------------
    if (activeUpload === "urls") {
      if (trimmedUrls.length === 0 || trimmedUrls.every((u) => u === "")) {
        showErrorToast("Please add at least one Web URL.");
        return;
      }

      if (trimmedUrls.some((u) => u === "")) {
        showErrorToast("Please fill all URL(s) fields before submitting.");
        setUploadStatus("error");
        return;
      }

      const invalidWebUrls = trimmedUrls.filter((u) => !isValidHttpUrl(u));
      if (invalidWebUrls.length > 0) {
        console.warn("❌ Invalid Web URLs:", invalidWebUrls);
        showErrorToast("Invalid Web URL detected.");
        return;
      }

      const youtubeInWeb = trimmedUrls.filter(isYouTube);
      if (youtubeInWeb.length > 0) {
        console.warn("🎥 YouTube URL in Web tab:", youtubeInWeb);
        showErrorToast("Video URL detected. Please add it under Video upload.");
        return;
      }
    }

    // --------------------------------------------------
    // ✅ VIDEO TAB VALIDATION
    // --------------------------------------------------
    if (activeUpload === "videos") {
      if (trimmedVideos.length === 0 || trimmedVideos.every((v) => v === "")) {
        showErrorToast("Please add at least one Video URL(s).");
        return;
      }

      if (trimmedVideos.some((v) => v === "")) {
        showErrorToast(
          "Please fill all Video URL(s) fields before submitting.",
        );
        setUploadStatus("error");
        return;
      }

      const invalidVideoUrls = trimmedVideos.filter((v) => !isValidHttpUrl(v));
      if (invalidVideoUrls.length > 0) {
        console.warn("❌ Invalid Video URLs:", invalidVideoUrls);
        showErrorToast("Invalid Video URL detected.");
        return;
      }

      const nonYoutubeInVideo = trimmedVideos.filter((v) => !isYouTube(v));
      if (nonYoutubeInVideo.length > 0) {
        console.warn("🌐 Web URL in Video tab:", nonYoutubeInVideo);
        showErrorToast("Web URL detected. Please add it under Web URL upload.");
        return;
      }
    }

    // --------------------------------------------------
    // ✅ ORIGINAL LOGIC (UNCHANGED)
    // --------------------------------------------------
    const validUrls = trimmedUrls.filter(
      (link) => link !== "" && !isYouTube(link),
    );

    const validVideos = trimmedVideos
      .concat(trimmedUrls.filter(isYouTube))
      .filter((link) => link !== "");

    const hasFiles = files.length > 0;
    const hasUrls = validUrls.length > 0;
    const hasVideos = validVideos.length > 0;

    if (!hasFiles && !hasUrls && !hasVideos) {
      showErrorToast("Please select at least one Document");
      setUploadStatus("error");
      return;
    }

    let uploadedInfo = {};
    if (hasFiles) uploadedInfo = { type: "Document", count: files.length };
    else if (hasUrls)
      uploadedInfo = { type: "Web URL", count: validUrls.length };
    else if (hasVideos)
      uploadedInfo = { type: "Video URL", count: validVideos.length };

    setUploadedInfo(uploadedInfo);
    setShowFirstModal(true);
  };

  const handleConfirm = async () => {
    try {
      // if (!selectedFolderId) {
      //   showErrorToast("Please select a folder.");
      //   return;
      // }

      // setIsUploading(true);
      // setShowFirstModal(false);
      // setUploadStatus("uploading");

      const user_id = localStorage.getItem("session_id");
      const token = localStorage.getItem("access_token");

      const headers = { Authorization: `Bearer ${token}` };
      const requests = [];

      const endpoints = {
        urls: `${API_BASE}/uploaded-files/newUpload_webpage`,
        videos: `${API_BASE}/uploaded-files/newUpload_youtube`,
      };

      const isYouTube = (link) =>
        link.includes("youtube.com") || link.includes("youtu.be");

      // CLEAN URL LISTS
      const validUrls = urlLinks.filter(
        (link) => link.trim() !== "" && !isYouTube(link),
      );

      const validVideos = [
        ...new Set(videoLinks.concat(urlLinks.filter(isYouTube))),
      ].filter((link) => link.trim() !== "");

      const hasFiles = files.length > 0;
      const hasRealUrls = validUrls.length > 0;
      const hasRealVideos = validVideos.length > 0;

      // NOTHING SELECTED
      if (!hasFiles && !hasRealUrls && !hasRealVideos) {
        showErrorToast("Please select at least one file.");
        setUploadStatus("error");
        return;
      }

      // ------------------------------------------------------------
      // 0️⃣ PROCESSING STARTED (Only URLs / Videos)
      // ------------------------------------------------------------
      if (hasRealUrls) {
        showSuccessToast("Web URL(s) upload started.");
      }
      if (hasRealVideos) {
        showSuccessToast("Video URL(s) upload started.");
      }
      // ------------------------------------------------------------
      //  FILE UPLOAD → REDUX ONLY
      // ------------------------------------------------------------

      // 1️⃣ Validate filenames BEFORE upload (non-blocking)
      let validFiles = [];
      let invalidFiles = [];

      if (hasFiles) {
        for (const file of files) {
          const { valid, reason } = validateFileName(file.name);

          if (valid) {
            validFiles.push(file);
          } else {
            invalidFiles.push({ name: file.name, reason });
          }
        }
      }

      // 2️⃣ Remove invalid files from selection (SKIP)
      if (invalidFiles.length > 0) {
        showErrorToast(
          `Skipped ${invalidFiles.length} invalid file(s): ${invalidFiles
            .map((f) => f.name)
            .join(", ")}`,
        );

        setFiles(validFiles);
      }

      // 3️⃣ If nothing valid left, stop
      if (hasFiles && validFiles.length === 0) {
        setUploadStatus("error");
        return;
      }

      // 2️⃣ NOW close modal
      setShowFirstModal(false);
      setUploadStatus("uploading");

      // ---- FILES → Add to Redux Queue
      if (hasFiles) {
        const jobId = Date.now() + Math.random();
        dispatch(
          addFiles({
            files: validFiles,
            meta: {
              user_id,
              model: selectedModel,
              mode: uploadMode,
              isFullPrivacy,
              description: Description,
              folder_id: selectedFolderId,
              jobId,
            },
          }),
        );

        dispatch(setStatus("queued"));
        setShowFolderModal(false);
        setSelectedFolderId({
          id: null,
          name: "Root",
        });
        setShowFirstModal(false);
        // setTimeout(() => {
        //   showSuccessToast("Added to upload queue");
        // }, 1000);

        setdescription("");
        setFiles([]);
      }

      // ------------------------------------------------------------
      //  URL UPLOAD
      // ------------------------------------------------------------
      if (hasRealUrls) {
        const payload = {
          urls: validUrls,
          model: selectedModel,
          mode: uploadMode.toLowerCase(),
          deep_crawl: true,
          description: Description,
        };

        requests.push(
          api.post(endpoints.urls, payload, {
            headers: { ...headers, "Content-Type": "application/json" },
          }),
        );
        setFiles([]);
        setUrlLinks([""]);
        setVideoLinks([""]);
        setdescription("");
      }

      // ------------------------------------------------------------
      // VIDEO UPLOAD
      // ------------------------------------------------------------
      if (hasRealVideos) {
        const payload = {
          user_id,
          youtube_urls: validVideos,
          mode: uploadMode.toLowerCase(),
          model: selectedModel,
          description: Description,
        };

        requests.push(
          api.post(endpoints.videos, payload, {
            headers: {
              ...headers,
              "Content-Type": "application/json",
            },
          }),
        );
        setFiles([]);
        setUrlLinks([""]);
        setVideoLinks([""]);
        setdescription("");
      }

      // ------------------------------------------------------------
      // RUN API REQUESTS
      // ------------------------------------------------------------

      if (requests.length > 0) {
        const formatList = (items = [], max = 3) => {
          const safe = (Array.isArray(items) ? items : []).filter(Boolean);
          if (safe.length <= max) return safe.join(", ");
          return `${safe.slice(0, max).join(", ")} +${safe.length - max} more`;
        };

        const getUploadKindFromUrl = (url = "") => {
          const u = String(url);
          if (u.includes("newUpload_webpage")) return "Web URL";
          if (u.includes("newUpload_youtube")) return "Video URL";
          return "URL";
        };

        const showBackendToast = (toastObj) => {
          if (!toastObj?.message) return;
          if (toastObj.type === "warning")
            return showInfoToast(toastObj.message);
          if (toastObj.type === "error")
            return showErrorToast(toastObj.message);
          return showSuccessToast(toastObj.message);
        };

        const handleApiData = (data, kind) => {
          if (data?.success === false) {
            if (data?.toast) showBackendToast(data.toast);
            else if (
              Array.isArray(data?.duplicate_urls) &&
              data.duplicate_urls.length
            ) {
              showErrorToast(
                `Duplicate ${kind}(s): ${formatList(data.duplicate_urls)}`,
              );
            } else if (data?.message) showErrorToast(data.message);
            else showErrorToast("Upload failed due to duplicates or errors.");
            return { ok: false };
          }

          if (data?.toast) showBackendToast(data.toast);

          if (data?.status === "pending") {
            showSuccessToast(`${kind}(s) submitted for approval.`);
          }

          if (Array.isArray(data?.uploaded_urls) && data.uploaded_urls.length) {
            showSuccessToast(
              `${kind}(s) uploaded successfully: ${formatList(data.uploaded_urls)}`,
            );
          }

          if (
            Array.isArray(data?.duplicate_urls) &&
            data.duplicate_urls.length
          ) {
            showErrorToast(
              `Duplicate ${kind}(s): ${formatList(data.duplicate_urls)}`,
            );
          }

          if (Array.isArray(data?.failed_pages) && data.failed_pages.length) {
            showErrorToast(
              `Failed to process some ${kind}(s): ${formatList(
                data.failed_pages.map((p) =>
                  typeof p === "string" ? p : p?.url,
                ),
              )}`,
            );
          }

          return { ok: true };
        };

        try {
          const results = await Promise.allSettled(requests);

          // Unified handling for both Web + Video uploads (including duplicates)
          setFiles([]);
          setUrlLinks([""]);
          setVideoLinks([""]);
          setdescription("");

          let anyError = false;

          results.forEach((r) => {
            if (r.status === "fulfilled") {
              const res = r.value;
              const kind = getUploadKindFromUrl(res?.config?.url);
              const outcome = handleApiData(res?.data, kind);
              if (!outcome.ok) anyError = true;
              return;
            }

            anyError = true;
            const err = r.reason;
            const kind = getUploadKindFromUrl(err?.config?.url);
            const data = err?.response?.data;

            if (data?.toast) return showBackendToast(data.toast);
            if (
              Array.isArray(data?.duplicate_urls) &&
              data.duplicate_urls.length
            ) {
              return showErrorToast(
                `Duplicate ${kind}(s): ${formatList(data.duplicate_urls)}`,
              );
            }
            return showErrorToast(
              data?.message || "Please enter valid web/video URL(s).",
            );
          });

          setUploadStatus(anyError ? "error" : "success");
          return;

          /* unreachable legacy response handler (kept for reference)
            responses.forEach((res) => {
              const data = res.data;

              // 🔴 semantic error (even with 200)
              if (data?.success === false) {
                if (data.toast) {
                  // Use the message string from the toast object
                  showErrorToast(`${data.toast.message}`);
                } else if (data.message) {
                  showErrorToast(data.toast.message);
                } else {
                  showErrorToast("Upload failed due to duplicates or errors.");
                }
                setUploadStatus("error");
                return;
              }

              // 🟡 pending approval
              if (data?.status === "pending") {
                showSuccessToast("Upload submitted for approval.");
                setUploadStatus("success");
                return;
              }

              // 🟢 success
              if (data?.status === "success") {
                // Check which type of upload
                if (hasRealUrls && data.uploaded_urls?.length) {
                  showSuccessToast(
                    `Web URL(s) uploaded successfully: ${data.uploaded_urls.join(", ")}`,
                  );
                }
                if (hasRealVideos && data.uploaded_urls?.length) {
                  showSuccessToast(
                    `Video URL(s) uploaded successfully: ${data.uploaded_urls.join(", ")}`,
                  );
                }
                setUploadStatus("success");
              }
            });
          }
            */
        } catch (err) {
          const data = err?.response?.data;

          if (data?.toast) showBackendToast(data.toast);
          else if (
            Array.isArray(data?.duplicate_urls) &&
            data.duplicate_urls.length
          ) {
            showErrorToast(
              `Duplicate URL(s): ${formatList(data.duplicate_urls)}`,
            );
          } else
            showErrorToast(
              data?.message || "Please enter valid web/video URL(s).",
            );

          setUploadStatus("error");
        }
      }
    } catch (err) {
      console.error("❌ Upload error:", err);

      const backendMessage =
        err.response?.data?.message || "Please enter valid web/video URL(s).";

      showErrorToast(backendMessage);
    } finally {
      setUploadStatus((prev) => (prev === "uploading" ? "error" : prev));
    }
  };

  const getTitle = () => {
    switch (activeUpload) {
      case "urls":
        return "Upload Web URLs";
      case "videos":
        return "Upload Video URLs";
      default:
        return "Upload Documents";
    }
  };

  // ✅ Get icon
  const getIcon = () => {
    switch (activeUpload) {
      case "urls":
        return linkIcon;
      case "videos":
        return videoIcon;
      default:
        return fileIcon;
    }
  };

  const isValidUrl = (url) => {
    try {
      const parsed = new URL(url);
      // Optional: restrict to http or https only
      return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch (_) {
      return false;
    }
  };

  const handleProceedToFolderSelect = async () => {
    try {
      setIsFetchingFolders(true);

      await fetchFoldersForMode(uploadMode);

      setShowFirstModal(false);
      setShowFolderModal(true);
    } catch (err) {
      console.error(err);
      showErrorToast("Failed to load folders");
    } finally {
      setIsFetchingFolders(false);
    }
  };

  const fetchFoldersForMode = async (mode) => {
    const token = localStorage.getItem("access_token");

    const response = await api.get(
      `${API_BASE}/trained-documents/get-trained-docs`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    const folders = response.data.filter(
      (item) => item.itemType === "folder" && item.mode === mode.toLowerCase(),
    );

    setFolderItems(folders);
  };

  const renderUploadContent = () => {
    // if web url come this if will work
    if (activeUpload === "urls") {
      return (
        <>
          <div className="w-100 text-center">
            {/* Inner Dashed Box */}
            <div className="url-inner-box justify-content-center rounded-4 text-center position-relative p-3 p-md-5 overflow-hidden items-center border-2">
              {urlLinks.map((link, index) => {
                const value = link.trim();
                const isInvalid = value !== "" && !isValidUrl(value);

                return (
                  <div
                    key={index}
                    className="d-flex flex-column align-items-center gap-1 mb-3 w-100"
                  >
                    <div className="d-flex justify-content-center align-items-center gap-2 w-100">
                      <input
                        type="text"
                        className={`form-control ${
                          isInvalid ? "is-invalid" : ""
                        }`}
                        placeholder={
                          focusedIndex === index
                            ? ""
                            : "https://example.com/your-link"
                        }
                        value={link}
                        onFocus={() => setFocusedIndex(index)}
                        onBlur={() => setFocusedIndex(null)}
                        onChange={(e) => {
                          const newLinks = [...urlLinks];
                          newLinks[index] = e.target.value;
                          setUrlLinks(newLinks);
                        }}
                        required
                      />

                      {urlLinks.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-sm text-danger"
                          onClick={() =>
                            setUrlLinks(urlLinks.filter((_, i) => i !== index))
                          }
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Add More */}
              <div className="text-end mt-2">
                <div
                  className="d-flex align-items-center gap-1 justify-content-end add-more-btn"
                  onClick={() => setUrlLinks([...urlLinks, ""])}
                >
                  <span className="refresh-prompts">
                    <i className="bi bi-plus-circle"></i> Add More
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="description-title fw-semibold my-3 d-flex align-items-center">
            Description
          </div>

          <textarea
            className="form-control custom-textarea rounded-3 px-2 px-md-4 py-1 py-md-2"
            placeholder="Write here..."
            value={Description}
            onChange={(e) => setdescription(e.target.value)}
          />
        </>
      );
    }

    // if youtube url come this if will work
    if (activeUpload === "videos") {
      return (
        <>
          <div className="w-100 text-center">
            {/* <div className="url-outer-box rounded-4 p-3 p-md-5 border"> */}
            <div className="url-inner-box justify-content-center rounded-4 text-center position-relative p-3 p-md-5 overflow-hidden items-center border-2">
              {videoLinks.map((link, index) => {
                const value = link?.trim() || "";
                const isInvalid = value !== "" && !isValidUrl(value);

                return (
                  <div
                    key={index}
                    className="d-flex flex-column align-items-center gap-1 mb-3 w-100"
                  >
                    <div className="d-flex justify-content-center align-items-center gap-2 w-100">
                      <input
                        type="text"
                        className={`form-control ${
                          isInvalid ? "is-invalid" : ""
                        }`}
                        placeholder={
                          focusedIndex === index
                            ? ""
                            : "https://example.com/your-link"
                        }
                        value={link || ""}
                        onFocus={() => setFocusedIndex(index)}
                        onBlur={() => setFocusedIndex(null)}
                        onChange={(e) => {
                          const newLinks = [...videoLinks];
                          newLinks[index] = e.target.value;
                          setVideoLinks(newLinks);
                        }}
                        required
                      />

                      {videoLinks.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-sm text-danger"
                          onClick={() =>
                            setVideoLinks(
                              videoLinks.filter((_, i) => i !== index),
                            )
                          }
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Add More */}
              <div className="text-end mt-2">
                <div
                  className="d-flex align-items-center gap-1 justify-content-end add-more-btn"
                  onClick={() => setVideoLinks([...videoLinks, ""])}
                >
                  <span className="refresh-prompts">
                    <i className="bi bi-plus-circle"></i> Add More
                  </span>
                </div>
              </div>
            </div>
            {/* </div> */}
          </div>

          <div className="description-title fw-semibold my-3 d-flex align-items-center">
            Description
          </div>

          <textarea
            className="form-control custom-textarea rounded-3 px-2 px-md-4 py-1 py-md-2"
            placeholder="Write here..."
            value={Description}
            onChange={(e) => setdescription(e.target.value)}
          />
        </>
      );
    }

    // if document or file come this if will work
    return (
      <>
        <div>
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="drop-zone d-flex justify-content-center border-2 rounded-4 text-center position-relative p-1 p-md-2 p-lg-4 items-center align-items-center "
          >
            {files.length > 0 && (
              <div className="selected-files-badge refresh-prompts">
                Selected Files: {files.length}
              </div>
            )}

            <input
              type="file"
              id="fileInput"
              multiple
              accept=".pdf,.docx,.txt,.doc,.pptx,.xlsx,.mp3,.wav,.aac,.flac,.mp4,.mov,.avi,.mkv,.flv,.png,.jpg,.jpeg,.gif,.bmp,.tiff"
              onChange={handleFileChange}
              className="d-none bg-danger"
            />

            <div
              className={`drop-zone-inner d-flex flex-column align-items-center w-100 ${
                files.length === 0 ? "is-empty" : "has-files"
              }`}
              onClick={() => document.getElementById("fileInput").click()}
            >
              <div
                className={`drop-zone-content d-flex flex-column align-items-center m-0 gap-2 ${
                  files.length === 0 ? "is-empty" : "has-files"
                }`}
              >
                <div className="upload-icon-wrapper rounded-circle border border-2 d-flex justify-content-center align-items-center p-2 m-0">
                  <img src={uploadIcon} alt="Upload" className="icon-image1" />
                </div>

                <p className="cutsom-p m-0">Drag and Drop here</p>
                <p className="cutsom-p m-0">or</p>

                <button
                  type="button"
                  className="bg-secondary browse-btn m-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    document.getElementById("fileInput").click();
                  }}
                >
                  <i className="bi bi-folder2-open" />
                  Browse Files
                </button>
                {files.length > 0 && (
                  <div
                    className="uploaded-files custom-ak-scollbar w-100 d-flex justify-content-start"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* <div
                    className="uploaded-files custom-ak-scollbar w-100 d-flex align-items-center justify-content-center"
                   
                  > */}
                    <div className="d-flex flex-wrap gap-2 justify-content-center">
                      {files.map((file, index) => (
                        <div key={index} className="file-item">
                          <div className="file-card border rounded px-2 py-2 d-flex align-items-center shadow-sm">
                            <i className="bi bi-file-earmark refresh-prompts me-2"></i>

                            <span
                              className="file-name text-truncate"
                              title={file.name}
                            >
                              {file.name}
                            </span>

                            <button
                              type="button"
                              className="btn-close icon-image btn-sm ms-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemove(index);
                              }}
                            ></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="description-title fw-semibold my-3 d-flex align-items-center">
          Description
        </div>

        <textarea
          className="form-control custom-textarea rounded-3 px-2 px-md-4 py-1 py-md-2"
          placeholder="Write here..."
          value={Description}
          onChange={(e) => setdescription(e.target.value)}
        />
      </>
    );
  };

  return (
    <div className="container-fluid px-4 position-relative train-container m-0">
      {/* Main Title */}
      <div className="train-title-wrapper align-items-center d-flex justify-content-between gap-3 mt-2">
        <h5 className="fw-semibold mb-0 d-flex align-items-center  train-title">
          <img src={getIcon()} alt={getTitle()} className="me-2 theme-icon" />
          {getTitle()}
        </h5>

        <button
          type="button"
          className="repo-type-chip active"
          onClick={() => navigate("/user/repo-organizer")}
        >
          Repo Organiser
        </button>
      </div>

      {/* Tabs to switch */}
      <div className="gap-md-2 d-flex justify-content-center justify-content-md-end mb-1 upload-options-wrapper pb-2 pt-4">
        {activeUpload !== "urls" && (
          <>
            <span
              className="upload-option d-flex align-items-center"
              onClick={() => {
                setActiveUpload("urls");
                setFiles([]);
                setUrlLinks([""]);
                setVideoLinks([""]);
                setdescription("");
              }}
            >
              <img src={linkIcon} className="me-2 theme-icon" /> Upload Web URLs
            </span>
            <span className="separator">|</span>
          </>
        )}

        {activeUpload !== "videos" && (
          <>
            <span
              className="upload-option d-flex align-items-center"
              onClick={() => {
                setActiveUpload("videos");
                setFiles([]);
                setUrlLinks([""]);
                setVideoLinks([""]);
                setdescription("");
              }}
            >
              <img src={videoIcon} className="me-2 theme-icon" /> Upload Video
              URLs
            </span>
            <span className="separator">|</span>
          </>
        )}

        {activeUpload !== "files" && (
          <>
            <span
              className="upload-option d-flex align-items-center"
              onClick={() => {
                setActiveUpload("files");
                setFiles([]);
                setUrlLinks([""]);
                setVideoLinks([""]);
                setdescription("");
              }}
            >
              <img src={fileIcon} className="me-2 theme-icon" /> Upload
              Documents
            </span>
            <span className="separator">|</span>
          </>
        )}
      </div>

      {/* Dynamic Upload Box */}
      <div className="training-upload-body">{renderUploadContent()}</div>

      {showFirstModal && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="document-modal modal-content text-center">
              {/* Header */}
              <div className="modal-header justify-content-center p-0 position-relative">
                <button
                  type="button"
                  className="modal-close-btn position-absolute"
                  aria-label="Close"
                >
                  <img
                    src={crossIcon}
                    alt="Close"
                    onClick={() => setShowFirstModal(false)}
                    className="icon-image modal-close-icon"
                  />
                </button>
              </div>

              {/* Body */}
              <div className="modal-body modal-body-padding">
                <div className="d-flex justify-content-center align-items-center mb-3">
                  <div className="modal-icon-wrapper rounded-circle d-flex justify-content-center align-items-center p-3">
                    <img
                      src={fileIcon}
                      alt="Upload"
                      className="text-black"
                      style={{ width: "36px", height: "36px" }}
                      // className="modal-file-icon"
                    />
                  </div>
                </div>

                <p className="refresh-prompts">
                  You have uploaded {uploadedInfo.count} {uploadedInfo.type}(s).
                  You are currently in <>{uploadMode} Mode</>.
                </p>

                <p className="refresh-prompts">
                  You can change the mode below if needed:
                </p>

                <div className="d-flex justify-content-center md:gap-4 ">
                  {["Global", "Personal"].map((mode) => (
                    <label
                      key={mode}
                      className="form-check custom-form-check d-flex align-items-center gap-2 pl-0"
                    >
                      <input
                        type="radio"
                        name="mode"
                        className="custom-radio"
                        value={mode}
                        checked={uploadMode === mode}
                        onChange={() => setUploadMode(mode)}
                      />
                      <span className="refresh-prompts">{mode}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="modal-footer justify-content-center gap-3">
                <button
                  type="button"
                  className="cancle-btn w-25 border border-1 fw-semibold"
                  onClick={() => setShowFirstModal(false)}
                >
                  Cancel
                </button>

                {/* <button className="custom-btn w-25" onClick={handleConfirm}>
                  Submit
                </button> */}
                <button
                  className="custom-btn w-25"
                  onClick={handleProceedToFolderSelect}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <UploadFolderModal
        show={showFolderModal}
        folders={folderItems}
        selectedFolderId={selectedFolderId}
        onSelectFolder={setSelectedFolderId}
        onClose={() => setShowFolderModal(false)}
        onConfirm={handleConfirm}
        loading={isUploading}
      />

      {(files.length > 0 ||
        urlLinks.some((u) => u.trim() !== "") ||
        videoLinks.some((v) => v.trim() !== "")) && (
        <div className="training-upload-actions d-flex flex-wrap justify-content-center gap-3 mb-4 ">
          <button
            className="cancle-btn px-5"
            onClick={() => {
              setFiles([]);
              setUrlLinks([""]);
              setVideoLinks([""]);
              setdescription("");
            }}
          >
            <span className="refresh-prompts">Cancel</span>
          </button>

          <button className="px-5 custom-btn" onClick={handleSubmit}>
            {files.length +
              urlLinks.filter((u) => u.trim() !== "").length +
              videoLinks.filter((v) => v.trim() !== "").length >
            1
              ? "Upload All"
              : "Upload"}
          </button>
        </div>
      )}

      {/* {activeUpload === "files" && <ProcessingQueue />} */}
    </div>
  );
};

export default DocumentUpload;
